import React, { useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Container,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  Fade,
  SelectChangeEvent,
  Grid,
  Stack,
} from "@mui/material";
import {
  ArrowForward,
  Send as SendIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  ChatBubble as ChatBubbleIcon,
  MailOutline,
  ScheduleOutlined,
} from "@mui/icons-material";
import { PageContainer } from "../components/common/StyledComponents";
import SurfaceCard from "../components/common/ui/SurfaceCard";
import FormField from "../components/common/ui/FormField";
import { useSendContact } from "../hooks/feedback";
import { ContactType } from "../api/feedback";
import BrandIcon from "../components/common/BrandIcon";

const FeedbackPage: React.FC = () => {
  const theme = useTheme();
  const [type, setType] = useState<ContactType>("OTHER");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { mutate: sendContact, isPending, isError, error } = useSendContact();

  const feedbackTypes = [
    {
      value: "BUG_REPORT" as ContactType,
      label: "Bug Report",
      icon: <BugReportIcon />,
      color: theme.palette.error.main,
      description: "Report a bug or technical issue",
    },
    {
      value: "FEATURE_REQUEST" as ContactType,
      label: "Feature Request",
      icon: <LightbulbIcon />,
      color: theme.palette.info.main,
      description: "Suggest a new feature or enhancement",
    },
    {
      value: "COMPLAINT" as ContactType,
      label: "Complaint",
      icon: <WarningIcon />,
      color: theme.palette.warning.main,
      description: "Share concerns or complaints",
    },
    {
      value: "QUESTION" as ContactType,
      label: "Question",
      icon: <HelpIcon />,
      color: theme.palette.success.main,
      description: "Ask a question or get help",
    },
    {
      value: "OTHER" as ContactType,
      label: "Other",
      icon: <ChatBubbleIcon />,
      color: theme.palette.secondary.main,
      description: "General feedback or comments",
    },
  ];

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value as ContactType);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSuccessMessage("");

    sendContact(
      { type, subject, message },
      {
        onSuccess: () => {
          setSuccessMessage("Thank you for contacting us! We'll get back to you soon.");
          setSubject("");
          setMessage("");
          setType("OTHER");
        },
      }
    );
  };

  const isFormValid = subject.trim() !== "" && message.trim() !== "";

  const selectedTypeInfo = feedbackTypes.find((t) => t.value === type);

  const selectFieldSx = {
    background: theme.palette.background.default,
    borderRadius: theme.radii?.medium ?? 10,
    color: theme.palette.text.primary,
    transition: "all 0.22s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.06)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.12)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
    "&.Mui-focused": {
      boxShadow: "0 8px 24px rgba(59,130,246,0.18)",
    },
    "& .MuiSelect-icon": {
      color: theme.palette.text.secondary,
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: 1.2,
    },
  };

  return (
    <PageContainer>
      <Box
        sx={{
          minHeight: "100%",
          py: { xs: 3, md: 5 },
          background: `radial-gradient(circle at 20% 10%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 36%), ${theme.palette.background.default}`,
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={650}>
            <SurfaceCard
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                background: alpha(theme.palette.background.paper, 0.9),
                backdropFilter: "blur(8px)",
                boxShadow: "0 14px 44px rgba(2, 6, 23, 0.55)",
              }}
            >
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    p: { xs: 2.6, sm: 3.2, md: 3.4 },
                    borderRight: { md: `1px solid ${alpha(theme.palette.text.primary, 0.08)}` },
                    borderBottom: { xs: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, md: "none" },
                    background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.background.paper, 0.35)})`,
                  }}
                >
                  <Box sx={{ mb: 2.2 }}>
                    <BrandIcon size={48} />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                    }}
                  >
                    Contact support
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, maxWidth: 360 }}>
                    Share your issue or idea and we will route it to the right team.
                  </Typography>

                  <Stack spacing={1.1} sx={{ mt: 2.4 }}>
                    <Chip
                      icon={<ScheduleOutlined />}
                      label="Most replies arrive within 1 business day"
                      sx={{
                        justifyContent: "flex-start",
                        borderRadius: "10px",
                        color: theme.palette.primary.light,
                        backgroundColor: alpha(theme.palette.primary.main, 0.16),
                      }}
                    />
                    <Chip
                      icon={<MailOutline />}
                      label="You can track updates from your inbox"
                      sx={{
                        justifyContent: "flex-start",
                        borderRadius: "10px",
                        color: theme.palette.text.primary,
                        backgroundColor: alpha(theme.palette.text.primary, 0.08),
                      }}
                    />
                  </Stack>

                  {selectedTypeInfo && (
                    <SurfaceCard
                      sx={{
                        mt: 2.2,
                        p: 1.8,
                        borderRadius: 2,
                        border: `1px solid ${alpha(selectedTypeInfo.color, 0.32)}`,
                        background: alpha(selectedTypeInfo.color, 0.11),
                      }}
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: alpha(selectedTypeInfo.color, 0.22),
                            color: selectedTypeInfo.color,
                          }}
                        >
                          {selectedTypeInfo.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {selectedTypeInfo.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedTypeInfo.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </SurfaceCard>
                  )}
                </Grid>

                <Grid item xs={12} md={7} sx={{ p: { xs: 2.6, sm: 3.2, md: 3.8 } }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Send your message
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.2 }}>
                    A clear subject and details help us respond faster.
                  </Typography>

                  {successMessage && (
                    <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ mb: 2, borderRadius: 2 }} role="status">
                      {successMessage}
                    </Alert>
                  )}

                  {isError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                      {(error as any)?.message || "We could not send your message. Please try again."}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <FormControl
                      fullWidth
                      sx={{
                        mb: 2.1,
                        "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
                        "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.primary.main },
                      }}
                    >
                      <InputLabel id="feedback-type-label">Message type</InputLabel>
                      <Select
                        labelId="feedback-type-label"
                        id="feedback-type"
                        value={type}
                        label="Message type"
                        onChange={handleTypeChange}
                        sx={selectFieldSx}
                      >
                        {feedbackTypes.map((feedbackType) => (
                          <MenuItem key={feedbackType.value} value={feedbackType.value}>
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 0.4 }}>
                              <Avatar
                                sx={{
                                  width: 26,
                                  height: 26,
                                  bgcolor: alpha(feedbackType.color, 0.18),
                                  color: feedbackType.color,
                                }}
                              >
                                {feedbackType.icon}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {feedbackType.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {feedbackType.description}
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormField
                      fullWidth
                      label="Subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      placeholder="Short summary"
                      inputProps={{ maxLength: 200 }}
                      helperText={`${subject.length}/200 characters`}
                      FormHelperTextProps={{ sx: { textAlign: "right", m: 0, mt: 0.75 } }}
                      sx={{ mb: 2.1 }}
                    />

                    <FormField
                      fullWidth
                      label="Message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      multiline
                      minRows={6}
                      maxRows={10}
                      placeholder="What happened, what you expected, and any details that help us reproduce or understand the issue quickly."
                      inputProps={{ maxLength: 2000 }}
                      helperText={`${message.length}/2000 characters`}
                      FormHelperTextProps={{ sx: { textAlign: "right", m: 0, mt: 0.75 } }}
                      sx={{
                        mb: 2.4,
                        "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
                          alignItems: "flex-start",
                          borderRadius: 2,
                          py: 0,
                        },
                        "& .MuiOutlinedInput-inputMultiline": {
                          padding: "16px 14px",
                          lineHeight: 1.55,
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={!isFormValid || isPending}
                      startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      endIcon={!isPending ? <ArrowForward /> : undefined}
                      sx={{
                        py: 1.35,
                        borderRadius: theme.radii?.medium ?? 10,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                        },
                      }}
                    >
                      {isPending ? "Sending..." : "Send message"}
                    </Button>

                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.4 }}>
                      Please avoid sensitive personal or payment details.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </SurfaceCard>
          </Fade>
        </Container>
      </Box>
    </PageContainer>
  );
};

export default FeedbackPage;

